import {
	useEffect,
	useId,
	useLayoutEffect,
	useRef,
	useState,
	type RefObject,
} from 'react';
import { ChevronRight } from 'lucide-react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';
import { PRONTUARIO_TRAY_ACTIVE_COLOR } from '@/constants/sessionCallDailyTheme';
import {
	COACH_SPOTLIGHT_RING_CLASS,
	computeTutorialHeaderNavSpotlight,
	isTutorialHeaderInteractionTarget,
	toCoachSpotlightRect,
	TUTORIAL_DEMO_HEADER_LAYOUT_SELECTORS,
	type CoachSpotlightRect,
} from './tutorialDemonstracaoCoachSpotlight';
import { TutorialDemonstracaoCoachBackdrop } from './TutorialDemonstracaoCoachBackdrop';

const JOIN_BALLOON_WIDTH_PX = 260;
const WELCOME_BALLOON_MAX_WIDTH_PX = 400;
const JOIN_BALLOON_GAP_PX = 10;

const MiniEntrarLabel = () => (
	<span
		className="mx-0.5 inline-flex rounded-md px-1.5 py-0.5 align-middle text-xs font-medium leading-none text-[#0f1722]"
		style={{ backgroundColor: PRONTUARIO_TRAY_ACTIVE_COLOR }}
	>
		Entrar
	</span>
);

type BalloonPosition = {
	top: number;
	left: number;
	arrowLeft: number;
	spotlight: CoachSpotlightRect;
};

type CoachStep = 'welcome' | 'join';

type Props = {
	joinButtonRef: RefObject<HTMLButtonElement | null>;
	onStepChange?: (step: CoachStep) => void;
};

const TutorialDemonstracaoPrejoinCoachBalloon = ({
	joinButtonRef,
	onStepChange,
}: Props) => {
	const welcomeMaskId = useId().replace(/:/g, '');
	const joinMaskId = useId().replace(/:/g, '');
	const portalRef = useRef<HTMLDivElement>(null);
	const [step, setStep] = useState<CoachStep>('welcome');
	const [joinPosition, setJoinPosition] = useState<BalloonPosition | null>(
		null,
	);
	const [headerNavSpotlight, setHeaderNavSpotlight] =
		useState<CoachSpotlightRect | null>(null);

	const goToJoinStep = () => {
		setStep('join');
		onStepChange?.('join');
	};

	useLayoutEffect(() => {
		if (step !== 'welcome' && step !== 'join') {
			setHeaderNavSpotlight(null);
			return;
		}

		const updateHeaderNavSpotlight = () => {
			setHeaderNavSpotlight(computeTutorialHeaderNavSpotlight());
		};

		updateHeaderNavSpotlight();

		const observer = new ResizeObserver(updateHeaderNavSpotlight);
		const mutationObserver = new MutationObserver(updateHeaderNavSpotlight);

		for (const selector of TUTORIAL_DEMO_HEADER_LAYOUT_SELECTORS) {
			document
				.querySelectorAll<HTMLElement>(selector)
				.forEach((element) => observer.observe(element));
		}

		mutationObserver.observe(document.body, {
			childList: true,
			subtree: true,
			attributes: true,
		});

		window.addEventListener('resize', updateHeaderNavSpotlight);
		window.addEventListener('scroll', updateHeaderNavSpotlight, true);

		return () => {
			observer.disconnect();
			mutationObserver.disconnect();
			window.removeEventListener('resize', updateHeaderNavSpotlight);
			window.removeEventListener(
				'scroll',
				updateHeaderNavSpotlight,
				true,
			);
		};
	}, [step]);

	useLayoutEffect(() => {
		if (step !== 'join') {
			setJoinPosition(null);
			return;
		}

		const updatePosition = () => {
			const button = joinButtonRef.current;
			if (!button) return;

			const rect = button.getBoundingClientRect();
			const left = Math.min(
				Math.max(12, rect.right - JOIN_BALLOON_WIDTH_PX + 8),
				window.innerWidth - JOIN_BALLOON_WIDTH_PX - 12,
			);
			const top = rect.top - JOIN_BALLOON_GAP_PX;
			const arrowLeft = rect.left + rect.width / 2 - left;

			setJoinPosition({
				top,
				left,
				arrowLeft: Math.min(
					Math.max(20, arrowLeft),
					JOIN_BALLOON_WIDTH_PX - 20,
				),
				spotlight: toCoachSpotlightRect(button),
			});
		};

		updatePosition();

		const observer = new ResizeObserver(updatePosition);
		const button = joinButtonRef.current;
		if (button) observer.observe(button);

		window.addEventListener('resize', updatePosition);
		window.addEventListener('scroll', updatePosition, true);

		return () => {
			observer.disconnect();
			window.removeEventListener('resize', updatePosition);
			window.removeEventListener('scroll', updatePosition, true);
		};
	}, [joinButtonRef, step]);

	useEffect(() => {
		const isAllowedInteractionTarget = (node: Node) => {
			const continueButton = portalRef.current?.querySelector(
				'button[aria-label="Continuar"]',
			);
			if (continueButton?.contains(node)) return true;
			if (isTutorialHeaderInteractionTarget(node)) return true;

			if (step === 'join') {
				const joinButton = joinButtonRef.current;
				if (joinButton?.contains(node)) return true;
			}

			return false;
		};

		const blockDisallowedInteraction = (event: Event) => {
			const target = event.target;
			if (!(target instanceof Node)) return;
			if (isAllowedInteractionTarget(target)) return;

			event.preventDefault();
			event.stopPropagation();
		};

		document.addEventListener(
			'pointerdown',
			blockDisallowedInteraction,
			true,
		);
		document.addEventListener('click', blockDisallowedInteraction, true);

		return () => {
			document.removeEventListener(
				'pointerdown',
				blockDisallowedInteraction,
				true,
			);
			document.removeEventListener(
				'click',
				blockDisallowedInteraction,
				true,
			);
		};
	}, [joinButtonRef, step]);

	if (step === 'welcome') {
		const welcomeHoles = headerNavSpotlight ? [headerNavSpotlight] : [];

		return createPortal(
			<div
				ref={portalRef}
				className="pointer-events-none fixed inset-0 z-[200] flex items-center justify-center p-4"
			>
				{welcomeHoles.length > 0 ? (
					<TutorialDemonstracaoCoachBackdrop
						maskId={welcomeMaskId}
						holes={welcomeHoles}
					/>
				) : (
					<div className="absolute inset-0 bg-black/55" aria-hidden />
				)}
				{headerNavSpotlight ? (
					<div
						className={COACH_SPOTLIGHT_RING_CLASS}
						style={{
							top: headerNavSpotlight.top,
							left: headerNavSpotlight.left,
							width: headerNavSpotlight.width,
							height: headerNavSpotlight.height,
						}}
						aria-hidden
					/>
				) : null}
				<div
					className="pointer-events-auto relative w-full rounded-xl border border-zinc-200/90 bg-white px-4 pb-3 pt-3 shadow-2xl"
					style={{ maxWidth: WELCOME_BALLOON_MAX_WIDTH_PX }}
					role="dialog"
					aria-labelledby="demo-prejoin-coach-welcome"
				>
					<h2
						id="demo-prejoin-coach-welcome"
						className="pr-10 text-base font-semibold text-zinc-900"
					>
						Bem-vindo
					</h2>
					<p className="mt-1 pr-10 text-sm leading-snug text-zinc-600">
						Esta é a sala de espera antes da videochamada — o mesmo
						das consultas reais na Plumaa. Aqui você pode configurar
						a câmera, o microfone e os alto-falantes antes de
						entrar. Você pode clicar no botão de voltar no canto
						superior esquerdo a qualquer momento para sair da
						demonstração.
					</p>
					<button
						type="button"
						onClick={goToJoinStep}
						className={cn(
							'absolute bottom-3 right-3 flex size-7 items-center justify-center rounded-full',
							'bg-zinc-900 text-white transition-colors hover:bg-zinc-800',
						)}
						aria-label="Continuar"
					>
						<ChevronRight className="size-3.5" strokeWidth={2.5} />
					</button>
				</div>
			</div>,
			document.body,
		);
	}

	if (!joinPosition) return null;

	const joinBackdropHoles = headerNavSpotlight
		? [joinPosition.spotlight, headerNavSpotlight]
		: [joinPosition.spotlight];

	return createPortal(
		<div
			ref={portalRef}
			className="pointer-events-none fixed inset-0 z-[200]"
		>
			<TutorialDemonstracaoCoachBackdrop
				maskId={joinMaskId}
				holes={joinBackdropHoles}
			/>
			<div
				className={COACH_SPOTLIGHT_RING_CLASS}
				style={{
					top: joinPosition.spotlight.top,
					left: joinPosition.spotlight.left,
					width: joinPosition.spotlight.width,
					height: joinPosition.spotlight.height,
				}}
				aria-hidden
			/>
			<div
				className="pointer-events-none absolute"
				style={{
					top: joinPosition.top,
					left: joinPosition.left,
					width: JOIN_BALLOON_WIDTH_PX,
					transform: 'translateY(-100%)',
				}}
				role="status"
				aria-live="polite"
			>
				<div className="rounded-xl border border-zinc-200/90 bg-white p-3.5 shadow-2xl">
					<p className="text-sm leading-relaxed text-zinc-600">
						Quando estiver pronto, clique em <MiniEntrarLabel />
						para iniciar a chamada com um paciente teste. Lá você
						terá o prontuário e as demais ferramentas da sessão.
					</p>
				</div>
				<div
					className="absolute -bottom-[7px] size-0 border-x-[9px] border-t-[9px] border-x-transparent border-t-white"
					style={{ left: joinPosition.arrowLeft - 9 }}
					aria-hidden
				/>
			</div>
		</div>,
		document.body,
	);
};

export { TutorialDemonstracaoPrejoinCoachBalloon };
export type { CoachStep as TutorialDemonstracaoPrejoinCoachStep };
